import React, { useState, useEffect } from "react";
import { FaPlus, FaTrash, FaRulerCombined, FaExchangeAlt, FaTimes, FaCheck, FaChevronDown } from "react-icons/fa";
import { FiEdit2} from 'react-icons/fi';
import { getMetrics, createMetric, updateMetric, deleteMetric } from "../services/ClientService";
import { toast } from "react-toastify";
import ConfirmModal from "./ConfirmModal";

const MetricsManagement = () => {
  const [metrics, setMetrics] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  
  // Form states
  const [metricName, setMetricName] = useState("");
  const [metricLabel, setMetricLabel] = useState("");
  const [supportingMetricName, setSupportingMetricName] = useState("");
  const [supportingMetricLabel, setSupportingMetricLabel] = useState("");
  const [primaryToSupporting, setPrimaryToSupporting] = useState("");
  const [supportingToPrimary, setSupportingToPrimary] = useState("");
  
  const [editingMetric, setEditingMetric] = useState(null);
  const [errors, setErrors] = useState({});
  const [confirmModal, setConfirmModal] = useState({ visible: false, message: '', onConfirm: null });

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    setIsLoading(true);
    try {
      const response = await getMetrics({});
      if (response.code === 200 && response.success) {
        setMetrics(response.data || []);
      } else {
        console.error("Failed to fetch metrics:", response);
      }
    } catch (error) {
      console.error("Error fetching metrics:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to evaluate math expressions like "1/1000"
  const evaluateExpression = (expr) => {
    try {
      if (!expr || expr.trim() === "") return null;
      // Remove spaces
      const cleanExpr = expr.trim();
      // Check if it contains division
      if (cleanExpr.includes('/')) {
        const parts = cleanExpr.split('/');
        if (parts.length === 2) {
          const numerator = parseFloat(parts[0]);
          const denominator = parseFloat(parts[1]);
          if (!isNaN(numerator) && !isNaN(denominator) && denominator !== 0) {
            return numerator / denominator;
          }
        }
      }
      // Otherwise try to parse as float
      const num = parseFloat(cleanExpr);
      return isNaN(num) ? null : num;
    } catch (error) {
      return null;
    }
  };

  const resetForm = () => {
    setMetricName("");
    setMetricLabel("");
    setSupportingMetricName("");
    setSupportingMetricLabel("");
    setPrimaryToSupporting("");
    setSupportingToPrimary("");
    setErrors({});
    setModalStep(1);
    setEditingMetric(null);
  };

  const handleOpenModal = () => {
    resetForm();
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!metricName.trim()) {
      newErrors.metricName = "Primary metric name is required";
    }
    if (!metricLabel.trim()) {
      newErrors.metricLabel = "Primary metric label is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!supportingMetricName.trim()) {
      newErrors.supportingMetricName = "Supporting metric name is required";
    }
    if (!supportingMetricLabel.trim()) {
      newErrors.supportingMetricLabel = "Supporting metric label is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    const primaryValue = evaluateExpression(primaryToSupporting);
    const supportingValue = evaluateExpression(supportingToPrimary);
    
    if (primaryValue === null || primaryValue <= 0) {
      newErrors.primaryToSupporting = "Please enter a valid positive number or expression (e.g., 1000 or 1000/1)";
    }
    if (supportingValue === null || supportingValue <= 0) {
      newErrors.supportingToPrimary = "Please enter a valid positive number or expression (e.g., 0.001 or 1/1000)";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveStep1 = () => {
    if (validateStep1()) {
      setModalStep(2);
    }
  };

  const handleSaveStep2 = () => {
    if (validateStep2()) {
      setModalStep(3);
    }
  };

  const handleSkipSupportingMetric = async () => {
    if (validateStep1()) {
      setIsLoading(true);
      try {
        const data = {
          metricName: metricName.trim(),
          metricLabel: metricLabel.trim(),
          supportingMetricName: null,
          supportingMetricLabel: null,
          primaryToSupporting: null,
          supportingToPrimary: null
        };

        let response;
        if (editingMetric) {
          // Update existing metric
          response = await updateMetric({
            metricId: editingMetric._id,
            ...data
          });
          if (response.code === 200 && response.success) {
            toast.success("Metric updated successfully!");
            fetchMetrics();
            handleCloseModal();
          } else {
            toast.error(response.error || "Failed to update metric");
          }
        } else {
          // Create new metric
          response = await createMetric(data);
          if (response.code === 201 && response.success) {
            toast.success("Metric created successfully!");
            fetchMetrics();
            handleCloseModal();
          } else {
            toast.error(response.error || "Failed to create metric");
          }
        }
      } catch (error) {
        console.error(editingMetric ? "Error updating metric:" : "Error creating metric:", error);
        toast.error(error.message || (editingMetric ? "Failed to update metric" : "Failed to create metric"));
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSubmit = async () => {
    if (validateStep3()) {
      setIsLoading(true);
      try {
        const data = {
          metricName: metricName.trim(),
          metricLabel: metricLabel.trim(),
          supportingMetricName: supportingMetricName.trim(),
          supportingMetricLabel: supportingMetricLabel.trim(),
          primaryToSupporting: evaluateExpression(primaryToSupporting),
          supportingToPrimary: evaluateExpression(supportingToPrimary)
        };

        let response;
        if (editingMetric) {
          // Update existing metric
          response = await updateMetric({
            metricId: editingMetric._id,
            ...data
          });
          if (response.code === 200 && response.success) {
            toast.success("Metric updated successfully!");
            fetchMetrics();
            handleCloseModal();
          } else {
            toast.error(response.error || "Failed to update metric");
          }
        } else {
          // Create new metric
          response = await createMetric(data);
          if (response.code === 201 && response.success) {
            toast.success("Metric created successfully!");
            fetchMetrics();
            handleCloseModal();
          } else {
            toast.error(response.error || "Failed to create metric");
          }
        }
      } catch (error) {
        console.error(editingMetric ? "Error updating metric:" : "Error creating metric:", error);
        toast.error(error.message || (editingMetric ? "Failed to update metric" : "Failed to create metric"));
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleEditMetric = (metric) => {
    setEditingMetric(metric);
    setMetricName(metric.metricName);
    setMetricLabel(metric.metricLabel);
    setSupportingMetricName(metric.supportingMetricName || "");
    setSupportingMetricLabel(metric.supportingMetricLabel || "");
    setPrimaryToSupporting(metric.primaryToSupporting ? metric.primaryToSupporting.toString() : "");
    setSupportingToPrimary(metric.supportingToPrimary ? metric.supportingToPrimary.toString() : "");
    
    // Determine which step to start on
    if (metric.supportingMetricName) {
      setModalStep(3); // If has supporting metric, go to conversion step
    } else {
      setModalStep(1); // Otherwise start from beginning
    }
    setShowModal(true);
  };

  const handleDeleteMetric = (metricId, metricName, isDefault) => {
    if (isDefault) {
      toast.error("Cannot delete the default Unit metric");
      return;
    }

    setConfirmModal({
      visible: true,
      message: `Are you sure you want to delete the metric "${metricName}"?`,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, visible: false }));
        setIsLoading(true);
        try {
          const response = await deleteMetric({ metricId });
          if (response.code === 200 && response.success) {
            toast.success("Metric deleted successfully!");
            fetchMetrics();
          } else {
            toast.error(response.error || "Failed to delete metric");
          }
        } catch (error) {
          console.error("Error deleting metric:", error);
          toast.error(error.message || "Failed to delete metric");
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white mb-4">
          <FaRulerCombined className="text-3xl" />
        </div>
        <h3 className="text-2xl font-bold text-gray-800">Define Your Primary Metric</h3>
        <p className="text-gray-600 mt-2">Set up the main unit of measurement for your inventory</p>
      </div>

      <div className="space-y-5">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Primary Metric Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={metricName}
            onChange={(e) => {
              setMetricName(e.target.value);
              if (errors.metricName) {
                setErrors(prev => ({ ...prev, metricName: undefined }));
              }
            }}
            placeholder="e.g., Kilogram, Liter, Piece"
            className={`w-full px-4 py-3 border-2 ${errors.metricName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all`}
          />
          {errors.metricName && (
            <p className="text-red-500 text-sm mt-1">{errors.metricName}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">This is the full name of your measurement unit</p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Short Label <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={metricLabel}
            onChange={(e) => {
              setMetricLabel(e.target.value);
              if (errors.metricLabel) {
                setErrors(prev => ({ ...prev, metricLabel: undefined }));
              }
            }}
            placeholder="e.g., Kg, L, Pc"
            className={`w-full px-4 py-3 border-2 ${errors.metricLabel ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all`}
          />
          {errors.metricLabel && (
            <p className="text-red-500 text-sm mt-1">{errors.metricLabel}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">Abbreviation shown in product listings (e.g., 1 Kg, 5 L)</p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-sm">
            i
          </div>
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">Tip for Retailers:</p>
            <p>Choose a metric that matches how you buy and sell your products. For example, if you buy rice in kilograms, use "Kilogram" as your primary metric.</p>
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <button
          onClick={handleCloseModal}
          className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200"
        >
          Cancel
        </button>
        <button
          onClick={handleSaveStep1}
          className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2"
        >
          Continue
          <FaChevronDown className="rotate-[-90deg]" />
        </button>
      </div>

      <div className="text-center pt-4 border-t border-gray-200">
        <button
          onClick={handleSkipSupportingMetric}
          className="text-blue-600 hover:text-blue-700 font-medium text-sm underline"
        >
          Skip - I don't need sub-units (e.g., for counting items like bottles, boxes)
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 text-white mb-4">
          <FaExchangeAlt className="text-3xl" />
        </div>
        <h3 className="text-2xl font-bold text-gray-800">Add Supporting Metric</h3>
        <p className="text-gray-600 mt-2">Define smaller units for precise measurements</p>
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3 text-sm text-gray-700">
          <div className="flex-shrink-0 px-3 py-1 bg-white rounded-full border-2 border-blue-500 font-bold text-blue-600">
            {metricLabel}
          </div>
          <FaChevronDown className="text-blue-500 rotate-[-90deg]" />
          <span className="font-medium">will be broken down into smaller units</span>
        </div>
      </div>

      <div className="space-y-5">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Supporting Metric Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={supportingMetricName}
            onChange={(e) => {
              setSupportingMetricName(e.target.value);
              if (errors.supportingMetricName) {
                setErrors(prev => ({ ...prev, supportingMetricName: undefined }));
              }
            }}
            placeholder="e.g., Gram, Milliliter, Individual Unit"
            className={`w-full px-4 py-3 border-2 ${errors.supportingMetricName ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all`}
          />
          {errors.supportingMetricName && (
            <p className="text-red-500 text-sm mt-1">{errors.supportingMetricName}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">The smaller unit name (e.g., if primary is Kilogram, supporting could be Gram)</p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Short Label <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={supportingMetricLabel}
            onChange={(e) => {
              setSupportingMetricLabel(e.target.value);
              if (errors.supportingMetricLabel) {
                setErrors(prev => ({ ...prev, supportingMetricLabel: undefined }));
              }
            }}
            placeholder="e.g., g, ml, unit"
            className={`w-full px-4 py-3 border-2 ${errors.supportingMetricLabel ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all`}
          />
          {errors.supportingMetricLabel && (
            <p className="text-red-500 text-sm mt-1">{errors.supportingMetricLabel}</p>
          )}
          <p className="text-xs text-gray-500 mt-1">Abbreviation for the supporting metric</p>
        </div>
      </div>

      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold text-sm">
            ?
          </div>
          <div className="text-sm text-purple-800">
            <p className="font-semibold mb-1">Example:</p>
            <p>If you sell flour in 1 Kg bags but also in 500 g packs, set Kilogram as primary and Gram as supporting metric.</p>
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <button
          onClick={() => setModalStep(1)}
          className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200"
        >
          Back
        </button>
        <button
          onClick={handleSaveStep2}
          className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2"
        >
          Continue
          <FaChevronDown className="rotate-[-90deg]" />
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-teal-600 text-white mb-4">
          <FaCheck className="text-3xl" />
        </div>
        <h3 className="text-2xl font-bold text-gray-800">Set Conversion Rates</h3>
        <p className="text-gray-600 mt-2">Define how to convert between your metrics</p>
      </div>

      <div className="bg-gradient-to-r from-green-50 to-teal-50 border border-green-200 rounded-lg p-5 mb-6">
        <div className="flex items-center justify-center gap-3 text-lg font-bold text-gray-800">
          <span className="px-4 py-2 bg-white rounded-lg border-2 border-blue-500 text-blue-600">{metricLabel}</span>
          <FaExchangeAlt className="text-green-600" />
          <span className="px-4 py-2 bg-white rounded-lg border-2 border-purple-500 text-purple-600">{supportingMetricLabel}</span>
        </div>
      </div>

      <div className="space-y-5">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            1 {metricLabel} equals how many {supportingMetricLabel}? <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center gap-3">
            <span className="flex-shrink-0 px-4 py-3 bg-blue-100 border-2 border-blue-500 rounded-lg font-bold text-blue-700">
              1 {metricLabel}
            </span>
            <FaChevronDown className="text-gray-400 rotate-[-90deg]" />
            <input
              type="text"
              value={primaryToSupporting}
              onChange={(e) => {
                const value = e.target.value;
                setPrimaryToSupporting(value);
                
                // Auto-calculate the inverse conversion
                const evaluated = evaluateExpression(value);
                if (evaluated !== null && evaluated !== 0) {
                  const inverse = 1 / evaluated;
                  setSupportingToPrimary(inverse.toString());
                } else {
                  setSupportingToPrimary('');
                }
                
                if (errors.primaryToSupporting) {
                  setErrors(prev => ({ ...prev, primaryToSupporting: undefined }));
                }
              }}
              placeholder="e.g., 1000"
              className={`flex-1 px-4 py-3 border-2 ${errors.primaryToSupporting ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all`}
            />
            <span className="flex-shrink-0 px-4 py-3 bg-purple-100 border-2 border-purple-500 rounded-lg font-bold text-purple-700">
              {supportingMetricLabel}
            </span>
          </div>
          {errors.primaryToSupporting && (
            <p className="text-red-500 text-sm mt-1">{errors.primaryToSupporting}</p>
          )}
          {primaryToSupporting && evaluateExpression(primaryToSupporting) !== null && (
            <p className="text-green-600 text-sm mt-1 font-semibold">
              ✓ 1 {metricLabel} = {evaluateExpression(primaryToSupporting)} {supportingMetricLabel}
            </p>
          )}
          <p className="text-xs text-gray-500 mt-1">Example: 1 Kilogram = 1000 Grams</p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            1 {supportingMetricLabel} equals how many {metricLabel}? <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center gap-3">
            <span className="flex-shrink-0 px-4 py-3 bg-purple-100 border-2 border-purple-500 rounded-lg font-bold text-purple-700">
              1 {supportingMetricLabel}
            </span>
            <FaChevronDown className="text-gray-400 rotate-[-90deg]" />
            <input
              type="text"
              value={supportingToPrimary}
              disabled
              placeholder="Auto-calculated"
              className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
            />
            <span className="flex-shrink-0 px-4 py-3 bg-blue-100 border-2 border-blue-500 rounded-lg font-bold text-blue-700">
              {metricLabel}
            </span>
          </div>
          {errors.supportingToPrimary && (
            <p className="text-red-500 text-sm mt-1">{errors.supportingToPrimary}</p>
          )}
          {supportingToPrimary && evaluateExpression(supportingToPrimary) !== null && (
            <p className="text-green-600 text-sm mt-1 font-semibold">
              ✓ 1 {supportingMetricLabel} = {evaluateExpression(supportingToPrimary)} {metricLabel}
            </p>
          )}
          <p className="text-xs text-gray-500 mt-1">Example: 1 Gram = 0.001 Kilograms (or enter 1/1000)</p>
        </div>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-6">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white font-bold text-sm">
            ✓
          </div>
          <div className="text-sm text-green-800">
            <p className="font-semibold mb-1">Quick Tip:</p>
            <p>These conversion rates help the system automatically calculate inventory across different package sizes!</p>
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <button
          onClick={() => setModalStep(2)}
          className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200"
        >
          Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={isLoading}
          className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-lg font-semibold hover:from-green-700 hover:to-teal-700 shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
              Creating...
            </>
          ) : (
            <>
              <FaCheck />
              {editingMetric ? 'Update Metric' : 'Create Metric'}
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="h-screen overflow-hidden bg-gray-50">
      {isLoading && !showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-white/80 z-50">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}

      <div className="bg-white p-6 sticky top-0 z-10 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Measurement Metrics</h1>
            <p className="text-gray-600 mt-1">Manage units of measurement for your inventory</p>
          </div>
          <button
            onClick={handleOpenModal}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
          >
            <FaPlus />
            Add New Metric
          </button>
        </div>
      </div>

      <div className="px-6 pb-6 pt-6 overflow-y-auto max-h-[calc(100vh-140px)]">
        {metrics.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 mb-6">
              <FaRulerCombined className="text-5xl text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-700 mb-2">No Metrics Found</h3>
            <p className="text-gray-500 mb-6">Create your first measurement metric to get started</p>
            <button
              onClick={handleOpenModal}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 inline-flex items-center gap-2"
            >
              <FaPlus />
              Add Your First Metric
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {metrics.map((metric) => (
              <div
                key={metric._id}
                className={`bg-white rounded-2xl shadow-md hover:shadow-xl border-2 transition-all duration-300 overflow-hidden ${
                  metric.isDefault 
                    ? 'border-yellow-400 bg-gradient-to-br from-yellow-50 to-amber-50' 
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                {metric.isDefault && (
                  <div className="bg-gradient-to-r from-yellow-400 to-amber-500 text-white px-4 py-2 text-center font-semibold text-sm">
                    🌟 Default Metric
                  </div>
                )}
                
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-800 mb-1">{metric.metricName}</h3>
                      <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                        {metric.metricLabel}
                      </span>
                    </div>
                    {!metric.isDefault && (
                      <div className="flex gap-4">
                        <button
                          onClick={() => handleEditMetric(metric)}
                          className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 p-2 rounded-lg transition-all"
                          title="Edit metric"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          onClick={() => handleDeleteMetric(metric._id, metric.metricName, metric.isDefault)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-all"
                          title="Delete metric"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    )}
                  </div>

                  {metric.supportingMetricName ? (
                    <div className="space-y-3 mt-4">
                      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-gray-600 uppercase">Supporting Unit</span>
                          <FaExchangeAlt className="text-purple-500" />
                        </div>
                        <div className="flex items-center gap-2">
                          <h4 className="text-lg font-bold text-gray-800">{metric.supportingMetricName}</h4>
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-semibold">
                            {metric.supportingMetricLabel}
                          </span>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">1 {metric.metricLabel} =</span>
                          <span className="font-bold text-green-600">
                            {metric.primaryToSupporting} {metric.supportingMetricLabel}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600">1 {metric.supportingMetricLabel} =</span>
                          <span className="font-bold text-green-600">
                            {metric.supportingToPrimary} {metric.metricLabel}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 bg-gray-50 rounded-lg p-4 text-center">
                      <p className="text-gray-500 text-sm">
                        {metric.isDefault 
                          ? "Used for counting individual items (bottles, boxes, pieces, etc.)" 
                          : "No sub-units configured"}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Metric Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-[70%] max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
              <div className="flex justify-between items-center">
                <div className="flex justify-between gap-8 items-center">
                  <h2 className="text-2xl font-bold">{editingMetric ? 'Edit Metric' : 'Create New Metric'}</h2>
                  <div className="flex items-center gap-2 mt-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${modalStep >= 1 ? 'bg-white text-blue-600' : 'bg-blue-400 text-white'} font-bold text-sm`}>
                      1
                    </div>
                    <div className={`h-1 w-12 ${modalStep >= 2 ? 'bg-white' : 'bg-blue-400'} rounded`}></div>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${modalStep >= 2 ? 'bg-white text-blue-600' : 'bg-blue-400 text-white'} font-bold text-sm`}>
                      2
                    </div>
                    <div className={`h-1 w-12 ${modalStep >= 3 ? 'bg-white' : 'bg-blue-400'} rounded`}></div>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${modalStep >= 3 ? 'bg-white text-blue-600' : 'bg-blue-400 text-white'} font-bold text-sm`}>
                      3
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-all duration-200"
                >
                  <FaTimes className="text-2xl" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1">
              {modalStep === 1 && renderStep1()}
              {modalStep === 2 && renderStep2()}
              {modalStep === 3 && renderStep3()}
            </div>
          </div>
        </div>
      )}
      {confirmModal.visible && (
        <ConfirmModal
          visible={confirmModal.visible}
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(prev => ({ ...prev, visible: false }))}
          cancelButtonText="Cancel"
          confirmButtonText="Delete"
        />
      )}
    </div>
  );
};

export default MetricsManagement;
